import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {GeometryAttributeSetData, MeshGeometryAttributeData, MeshGeometryAttributeSetData} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeSetNode, meshGeometryAttributeSetLike} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import {meshGeometryAttributeLike} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {MeshGeometryAttributeLikeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {MeshGeometryAttributeSetLikeNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {Operators} from "#template/geometry-processing/geometry-graph"
import type {AttributeRefSet} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"
import {
    applyGeometryOperator,
    geometryAttributeSetDataToGeometryAttributeRefDataSet,
    geometryAttributeRefDataSetToGeometryAttributeSetData,
} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

// Single-variant operator (mesh-only): parameter type is concrete, no per-dim genericity.
export type TriangulateOperatorParameters = {
    name: string
    primaryAttribute: MeshGeometryAttributeLikeNode | null
    attributeSet: MeshGeometryAttributeSetLikeNode | null
}

export const triangulateOperatorParametersSchema = z.object({
    name: z.string(),
    primaryAttribute: z.nullable(meshGeometryAttributeLike),
    attributeSet: z.nullable(meshGeometryAttributeSetLike),
})

function applyTriangulateOperator(primaryAttribute: MeshGeometryAttributeData, attributeSet: MeshGeometryAttributeSetData): MeshGeometryAttributeSetData {
    const primaryAttrRefData = primaryAttribute.ref
    const attrRefDataSet = geometryAttributeSetDataToGeometryAttributeRefDataSet(attributeSet)

    const triangulatedAttrRefDataSet = applyGeometryOperator(
        (primaryAttrRef: AttributeRef, attrRefSet: AttributeRefSet) => Operators.triangulate(primaryAttrRef, attrRefSet),
        primaryAttrRefData,
        attrRefDataSet,
    )

    return geometryAttributeRefDataSetToGeometryAttributeSetData(triangulatedAttrRefDataSet, 2)
}

export abstract class GeometryTriangulateOperatorNode<
    out P extends TriangulateOperatorParameters = TriangulateOperatorParameters,
> extends GeometryAttributeSetNode<2, P> {
    protected override hasValidParameters(parameters: P): parameters is P & NonNullableParameters<P> {
        return parameters.primaryAttribute !== null && parameters.attributeSet !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<P>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeSetData<2> | null>; invalidator?: BuilderInlet<null>} {
        const {primaryAttribute, attributeSet} = parameters

        const [evaluatedPrimaryAttributeValid, evaluatedPrimaryAttributeInvalid] = scope.branch(evaluator.evaluate(primaryAttribute))
        const [evaluatedAttributeSetValid, evaluatedAttributeSetInvalid] = scope.branch(evaluator.evaluate(attributeSet))

        const geometryAttributeSetData = scope.pureLambda(
            scope.tuple(evaluatedPrimaryAttributeValid, evaluatedAttributeSetValid),
            ([primaryAttributeData, attributeSetData]) => applyTriangulateOperator(primaryAttributeData, attributeSetData),
            "applyTriangulateOperator",
        )

        return {
            evaluatedGeometryData: geometryAttributeSetData,
            invalidator: scope.phi(evaluatedPrimaryAttributeInvalid, evaluatedAttributeSetInvalid),
        }
    }
}
